import React, { FC, MutableRefObject, memo } from 'react';
import styled from 'styled-components';
import { DropdownToggle, Input } from 'reactstrap';
import Icon from '@minecraft.icon';
import { DropdownWithChildrenProps, DropdownWithChildrenContextType } from '../shared/types';
import { StyledClearButton, StyledClearIcon } from '../shared/styles';
import DropdownWithChildrenSelectionsResult from '../DropdownWithChildrenSelectionsResult';

const StyledDropdownToggle = styled(DropdownToggle)<{ $isDropdownTogglerInputFocused: boolean }>`
  background-color: ${(props): string => props.theme.form.backgroundColor};
  border: 0.0625rem solid ${(props): string => props.theme.colors.primary.ltGrey1};
  border-radius: 0.25rem;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  height: 2.375rem;
  justify-content: space-between;
  width: 100%;

  &:active:not([disabled]) {
    border-color: ${(props): string => props.theme.colors.primary.primary};
  }

  &[disabled] {
    color: ${(props) => props.theme.form.state.disabled.inputTextColor};
    background-color: ${(props) => props.theme.form.state.disabled.backgroundColor};
  }

  ${(props) =>
    props.$isDropdownTogglerInputFocused &&
    `
    border-color: ${props.theme.colors.primary.primary};
  `}
`;

const StyledInput = styled(Input)`
  height: 100%;

  &,
  &:active,
  &:focus,
  &:hover {
    border: 0;
    outline: 0;
    box-shadow: none;
  }

  &::placeholder {
    font-size: 0.875rem;
    color: ${(props): string => props.theme.form.placeholderColor};
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &:disabled {
    color: inherit;
    background-color: inherit;
  }
`;

const StyledDroupdownButtonGroup = styled.div`
  align-items: center;
  display: flex;
  margin: 0.375rem 0;
  height: calc(100% - 0.75rem);
`;

const StyledDropdownButton = styled.div`
  display: flex;
  border-left: 0.0625rem solid ${(props): string => props.theme.colors.primary.ltGrey1};
  height: 100%;
  width: 1.75rem;
`;

const StyledCaretIcon = styled(Icon)`
  flex-shrink: 0;
  margin: auto;
  height: 0.75rem;
  width: 0.75rem;
`;

type Props = {
  isDropdownTogglerInputFocused: boolean;
  isDisabled: DropdownWithChildrenProps['isDisabled'];
  isClearable: DropdownWithChildrenProps['isClearable'];
  withSelectionsResult: DropdownWithChildrenProps['withSelectionsResult'];
  id: DropdownWithChildrenProps['id'];
  placeholder: DropdownWithChildrenProps['placeholder'];
  selectionsResult: DropdownWithChildrenContextType['selectionsResult'];
  searchInputValue: DropdownWithChildrenContextType['searchInputValue'];
  selectionsResultType: DropdownWithChildrenProps['selectionsResultType'];
  handleSearchInputValueChange: DropdownWithChildrenContextType['handleSearchInputValueChange'];
  handleSelectedOptionsClear: DropdownWithChildrenContextType['handleSelectedOptionsClear'];
  togglerRef: MutableRefObject<HTMLInputElement>;
  toggleLabel: string;
  dataTestId: DropdownWithChildrenProps['dataTestId'];
  qaId?: string;
};

const DropdownWithChildrenTogglerInput: FC<Props> = ({
  isDropdownTogglerInputFocused,
  isDisabled,
  isClearable,
  withSelectionsResult,
  id,
  placeholder,
  selectionsResult,
  searchInputValue,
  selectionsResultType,
  handleSearchInputValueChange,
  handleSelectedOptionsClear,
  togglerRef,
  toggleLabel,
  dataTestId,
  qaId,
}) => {
  const isSelectionsResultVisible = !isDropdownTogglerInputFocused && Boolean(selectionsResult);

  return (
    <StyledDropdownToggle
      $isDropdownTogglerInputFocused={isDropdownTogglerInputFocused}
      disabled={isDisabled}
      tag="div"
      data-testid={`${dataTestId}-toggler-input`}
      data-qa-id={`${qaId}-toggler-input`}
      aria-label={toggleLabel}
      role="combobox"
    >
      <StyledInput
        innerRef={togglerRef}
        id={id}
        value={searchInputValue}
        placeholder={!isSelectionsResultVisible ? placeholder : undefined}
        disabled={isDisabled}
        name="autocomplete-input"
        data-qa-id="autocomplete"
        autoComplete="off"
        onChange={handleSearchInputValueChange}
      />
      {withSelectionsResult && isSelectionsResultVisible && (
        <DropdownWithChildrenSelectionsResult
          isPositionAbsolute
          selectionsResult={selectionsResult}
          selectionsResultType={selectionsResultType}
          handleSelectedOptionsClear={handleSelectedOptionsClear}
        />
      )}
      <StyledDroupdownButtonGroup>
        {isSelectionsResultVisible && isClearable && (
          <StyledClearButton onClick={handleSelectedOptionsClear} data-qa-id="clear-all-selection">
            <StyledClearIcon name="close" inheritColor />
          </StyledClearButton>
        )}
        <StyledDropdownButton>
          <StyledCaretIcon name="caret-1" inheritColor />
        </StyledDropdownButton>
      </StyledDroupdownButtonGroup>
    </StyledDropdownToggle>
  );
};

export default memo(DropdownWithChildrenTogglerInput);
